import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Link } from '../../../cdl/Link/Link';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../helpers/translate.helper';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useRole } from '../../hooks/useRole';
import { IconX } from '../../icons/cdl/X';
import { useFlags } from '../../launchDarkly/useFlags';
import { Box } from '../../ui/Box';

const PRO_FEATURES_BANNER_DISMISSED_KEY = 'pro-features-release-banner-dismissed';

type SidebarProFeaturesBannerVariant = 'customer' | 'supplier';

const PRO_FEATURES_BANNER_LINKS: Record<SidebarProFeaturesBannerVariant, string> = {
    customer: 'https://closelink.productlane.com/docs/ordering/share-schedule-with-suppliers',
    supplier: 'https://www.closelink.com/supplier-pro-launch',
};

const getCopy = (variant: SidebarProFeaturesBannerVariant) => {
    if (variant === 'customer') {
        return {
            heading: translate('sidebar.proFeaturesBanner.customer.heading'),
            description: translate('sidebar.proFeaturesBanner.customer.description'),
        };
    }

    return {
        heading: translate('sidebar.proFeaturesBanner.supplier.heading'),
        description: translate('sidebar.proFeaturesBanner.supplier.description'),
    };
};

export const SidebarProFeaturesBanner = () => {
    const role = useRole();
    const flags = useFlags();
    const [dismissed, setDismissed] = useLocalStorageState(PRO_FEATURES_BANNER_DISMISSED_KEY, false);
    let variant: SidebarProFeaturesBannerVariant | null = null;

    if (role.isCustomer()) {
        variant = 'customer';
    }

    if (role.isSupplier()) {
        variant = 'supplier';
    }

    if (!flags.proFeaturesReleaseBanner || !variant || dismissed) {
        return null;
    }

    const copy = getCopy(variant);
    const isCustomerGroupOwner = variant === 'customer' && role.hasAdminRights();
    const customerGroupId = isCustomerGroupOwner ? role.group?.id : undefined;

    return (
        <Box
            position="relative"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={8}
            width="100%"
            padding={12}
            borderRadius={8}
            backgroundColor="accent.background"
        >
            <Box display="flex" flexDirection="column" gap={4} width="100%">
                <Text variant="subtitle2" paddingRight={24}>
                    {copy.heading}
                </Text>

                <Text variant="extraSmall" color="foreground.muted">
                    {copy.description}
                </Text>
            </Box>

            {customerGroupId ? (
                <Link
                    to="/customer-groups/$groupId"
                    params={{ groupId: customerGroupId }}
                    search={{ tab: 'settings' }}
                    size="small"
                    style={{ fontSize: 12, lineHeight: '16px' }}
                >
                    {translate('sidebar.proFeaturesBanner.customer.cta')}
                </Link>
            ) : (
                <Link
                    href={PRO_FEATURES_BANNER_LINKS[variant]}
                    external
                    size="small"
                    style={{ fontSize: 12, lineHeight: '16px' }}
                >
                    {translate('sidebar.proFeaturesBanner.cta')}
                </Link>
            )}

            <Box position="absolute" top={8} right={8}>
                <IconButton
                    Icon={IconX}
                    size="small"
                    emphasis="transparent"
                    aria-label={translate('sidebar.proFeaturesBanner.dismiss')}
                    onClick={() => {
                        setDismissed(true);
                    }}
                />
            </Box>
        </Box>
    );
};
